<template>
  <v-container class="p-5 mb-5">
    <v-row class="mb-2">
      <v-col cols="12" class="d-flex align-center">
        <h1 class="text-3xl font-bold">Website Analytics</h1>
        <span class="ml-4" style="color:#898781">Today</span>
        <v-spacer></v-spacer>
        <v-btn color="primary" href="/admin/analytics/search">
          Advanced Search
        </v-btn>
      </v-col>
    </v-row>

    <!-- Headline figures: all-time and today, independent of the range below -->
    <v-row dense class="mb-2">
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4">
          <div class="text-sm mb-1" style="color:#52514e">Total Page Views (All Time)</div>
          <div style="font-size:1.9rem; font-weight:700; line-height:1.2">{{ Number(totalPageViews).toLocaleString() }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4">
          <div class="text-sm mb-1" style="color:#52514e">Unique Visitors (All Time)</div>
          <div style="font-size:1.9rem; font-weight:700; line-height:1.2">{{ Number(totalUniqueVisitors).toLocaleString() }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4">
          <div class="text-sm mb-1" style="color:#52514e">Unique URLs (All Time)</div>
          <div style="font-size:1.9rem; font-weight:700; line-height:1.2">{{ Number(totalUniqueUrls).toLocaleString() }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="my-4"></v-divider>

    <v-progress-linear v-if="loading" indeterminate class="mb-4"></v-progress-linear>

    <traffic-summary-panels v-if="!loading" :data="summaryData" />

    <v-card class="mt-5">
      <v-card-title>Recent Visitors</v-card-title>
      <v-data-table
        :headers="recentVisitorsHeaders"
        :items="recentPageViews"
        class="elevation-1"
        disable-sort
      >
        <template v-slot:item.visited_at="{ item }">
          {{ formatDateTime(item.visited_at) }}
        </template>
        <template v-slot:item.path="{ item }">
          {{ truncateUrl(item.path) }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios';
import Layout from '@/Shared/VuetifyAdminLayout';
import TrafficSummaryPanels from '@/Shared/TrafficSummaryPanels';
import { buildRangePresets } from '@/Shared/rangePresets';

export default {
  layout: Layout,
  components: { TrafficSummaryPanels },
  props: {
    totalPageViews: { type: Number, default: 0 },
    totalUniqueVisitors: { type: Number, default: 0 },
    totalUniqueUrls: { type: Number, default: 0 },
  },
  data() {
    return {
      loading: true,
      summaryData: {},
      recentPageViews: [],
      recentVisitorsHeaders: [
        { text: 'Time', value: 'visited_at' },
        { text: 'Path', value: 'path' },
      ],
    };
  },
  computed: {
    rangeFilters() {
      // The dashboard is a fixed Today snapshot; period analysis lives
      // on the Advanced Search page.
      const today = buildRangePresets()[0];
      return {
        startDate: today.start,
        endDate: today.end,
      };
    },
  },
  methods: {
    async fetchSummary() {
      this.loading = true;
      try {
        const response = await axios.get('/admin/analytics/traffic-summary', {
          params: this.rangeFilters,
        });
        this.summaryData = response.data;
      } catch (error) {
        console.error('Error fetching traffic summary:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchRecentPageViews() {
      try {
        const response = await axios.get('/admin/analytics/recent-page-views?limit=20');
        this.recentPageViews = response.data;
      } catch (error) {
        console.error('Error fetching recent page views:', error);
      }
    },
    formatDateTime(datetime) {
      if (!datetime) return '';
      const date = new Date(datetime);
      return date.toLocaleString();
    },
    truncateUrl(url, maxLength = 50) {
      if (!url) return '';
      return url.length > maxLength ? url.substring(0, maxLength - 3) + '...' : url;
    },
  },
  mounted() {
    this.fetchSummary();
    this.fetchRecentPageViews();
  },
};
</script>
